// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import useConfigList from '@Hooks/useConfigList';
import { ProcedureOutput, ObjectTypeV1 as ObjectType, OrgPermissionsEnum, CaseOutput } from 'thehive-sdk';
import { useParams } from 'react-router';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import useCasePermissions from '@Hooks/useCasePermissions';
import { FormatMultipleDate } from '@Molecules/Format';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import useOneQuery, { ExtractDataQuery } from '@Hooks/useOneQuery';
import Details from '@Molecules/Details/Details';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { useLocalDico, useLocalMessage } from '@Helpers/locale';
import { useExtractData } from '@Pages/Admin/Entities/Details/AttackPatterns/List/configData';
import { ICONS } from '@Constants/iconsApp';
import { ttpEventList } from '@Organisms/TTP/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { Badge, Button } from 'antd';
import { classList } from '@Helpers/className';
import { TTPCreate } from '@Organisms/TTP/TTPCreate/TTPCreate';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useGenericCountQuery } from '@Queries/genericQueries';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { TTPDrawer } from '@Organisms/TTP/TTPDrawer/TTPDrawer';
import { PreviewButton } from '@Atoms/Buttons';
import { tactics } from '@Constants/procedure';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { HTMLColors } from '@Constants/colors';
import { useDrawer } from '@Hooks/useDrawer';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import ActionList from '@Molecules/ActionList/ActionList';
import { actionList } from '@Organisms/TTP/actionList';
import { computeGetSelector } from '@Organisms/Case/selector';
import usePageTitle from '@Hooks/usePageTitle';

import './TTPList.scss';

const TTPList = (): ReactElement => {
    const i = useLocalMessage();

    const { caseId } = useParams<{ caseId: string }>();

    const { visible, setVisible, data, setData } = useDrawer<ProcedureOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<ProcedureOutput>({});
    const {
        visible: visibleEdit,
        setVisible: setVisibleEdit,
        data: defaultValues,
        setData: setDefaultValues,
    } = useDrawer<ProcedureOutput>({});

    const config = useConfigList<ProcedureOutput>({
        selector: {
            head: [{ name: 'getCase', idOrName: caseId }, { name: 'procedures' }],
            extraData: ['pattern', 'patternParent', 'patternCatalog'],
            name: 'get-case-procedures',
            sort: ['-tactic'],
        },
        controller: client.ProcedureController,
        storageId: StorageFilterEnum.CASE_PROCEDURE,
        objectType: ObjectType.PROCEDURE,
        streamRootId: caseId,
    });

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        countRefetch,
        status = 'loading',
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        stateSelectedKeys: { selectedKeys },
        storagePageManager,
        storageViewManager,
        paginationManager,
        filterManager,
        table,
        rowSelectionCustomRenderer,
        queryParams,
    } = config;

    const { setDefaultDisplay } = storageViewManager || {};

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
    });

    const {
        page: storagePage,
        setDisplay,
        setFilterIsOpen,
        setAutoRefresh,
        setOnLoadView,
    } = storagePageManager || { page: null };

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        occurDate: { label: i('common.occurred-date'), visible: storagePage?.display?.occurDate ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        loadVisibleStateDates({
            occurDate: viewContent?.display?.occurDate ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const hasPermissions = useCasePermissions({
        caseId,
        permissionsNeeded: [OrgPermissionsEnum.MANAGE_PROCEDURE],
    });

    const columsTrad = useLocalDico(
        [
            'attackPattern.list.columns.patternId',
            'attackPattern.list.columns.name',
            'attackPattern.list.columns.parent-patternId',
            'attackPattern.list.columns.tactics',
            'attackPattern.list.columns.description',
            'common.preview',
            'attackPattern.preview.name',
            'attackPattern.preview.nameParent',
            'attackPattern.preview.url',
            'attackPattern.preview.dataSources',
            'attackPattern.preview.defenseBypassed',
            'attackPattern.preview.permissionsRequired',
            'attackPattern.preview.systemRequirements',
            'attackPattern.preview.remoteSupport',
            'attackPattern.preview.capecId',
            'attackPattern.preview.capecUrl',
            'attackPattern.preview.description',
            'attackPattern.preview.children',
            'attackPattern.preview.patternId',
            'attackPattern.preview.tactics',
        ],
        i,
    );
    const extractData = useExtractData(columsTrad);

    const onSuccess = useCallback(
        (count?: boolean, message?: string) => {
            listRefetch();
            table?.setSelectedKeys?.([]);
            if (count) {
                countRefetch();
            }
            if (message) {
                openMessage('success', message);
            }
        },
        [listRefetch, table, countRefetch],
    );

    const eventList = useMemo(
        () =>
            ttpEventList({
                controller: client.ProcedureController,
                locale: i,
                onError: handleNotificationError,
                onSuccess,
            }),
        [i, onSuccess],
    );

    const TTPActionList = useMemo(() => actionList({ i, eventList }), [i, eventList]);

    const columns = columnsRenderer([
        {
            title: [{ label: i('procedure.list.tactic'), sortField: 'tactic' }],
            className: 'ttp-list__column__tactic',
            render: ({ _id, tactic, extraData, ...rest }, { custom: filterCustom }): ReactElement => {
                const tacticParsed = tactic?.toLowerCase();

                const style = tacticParsed in tactics ? { color: tactics[tacticParsed].color } : {};

                const revoked = extraData?.pattern?.revoked;
                return revoked ? (
                    <span className="ttp-list__icon-warning">
                        <ICONS.WarningOutlined /> {i('procedure.list.revoked')}
                    </span>
                ) : !tactic ? (
                    <span
                        className="ttp-list__icon-missing"
                        onClick={() => {
                            setDefaultValues({ _id, tactic, extraData, ...rest });
                            setVisibleEdit(true);
                        }}
                    >
                        <ICONS.WarningOutlined /> {i('procedure.missing')}
                    </span>
                ) : (
                    <div
                        className="ttp-list__tactic"
                        onClick={(): void => filterCustom({ field: 'tactic', operator: 'any', values: [tactic] })}
                    >
                        <div
                            className="ttp-list__tactic__tag"
                            style={{ ['--color-tag' as string]: style.color || HTMLColors.DEFAULT }}
                        />
                        <span>{tactic}</span>
                    </div>
                );
            },
        },
        {
            title: [{ label: i('procedure.list.technique'), sortField: 'patternId' }],
            className: classList('ttp-list__column__technique', 'column_preview'),
            render: (procedure, { custom: filterCustom }): ReactElement => {
                return (
                    <div>
                        <THDrawer
                            title={procedure.extraData?.pattern?.name}
                            triggerElem={<Button type="link">{procedure.patternId}</Button>}
                        >
                            <ExtractDataQuery
                                selector={{
                                    head: [{ name: 'getPattern', idOrName: procedure.extraData?.pattern?.patternId }],
                                    name: `get-pattern-${procedure.extraData?.pattern?.patternId}`,
                                    extraData: ['children', 'parent'],
                                }}
                                controller={client.AttackPatternsController}
                            >
                                {(data: any): ReactElement => <Details data={data} extract={extractData} />}
                            </ExtractDataQuery>
                        </THDrawer>{' '}
                        - {procedure.extraData?.pattern?.name}{' '}
                        <ICONS.FilterTwoTone
                            onClick={(): void =>
                                filterCustom({ field: 'patternId', operator: 'any', values: [procedure.patternId] })
                            }
                        />
                        <div className="generic-drawer-preview">
                            <PreviewButton
                                onClick={() => {
                                    setData(procedure);
                                    setVisible(true);
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            title: i('procedure.list.by'),
            className: 'ttp-list__column__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'O.', sortField: 'occurDate', hide: !showDates.occurDate },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'ttp-list__column__date',
            render: ({ occurDate = 0, _createdAt = 0, _updatedAt = 0 }, { date: filterDate }) => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'O.',
                            value: occurDate,
                            onClick: (): void => filterDate(occurDate, 'occurDate'),
                            hide: !showDates.occurDate,
                        },
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'ttp-list__column__actions',
            hidden: !hasPermissions,
            render: ({ _id, ...rest }) => (
                <ActionsList
                    actionList={{
                        edit: {
                            label: i('general.edit'),
                            icon: <ICONS.EditIcon />,
                            query: () => {
                                setDefaultValues({ _id, ...rest });
                                setVisibleEdit(true);
                            },
                        },
                        delete: {
                            label: i('general.delete'),
                            message: i('procedure.list.actions.delete.single'),
                            confirm: true,
                            query: () => eventList.onDelete(_id),
                            icon: <ICONS.DeleteIcon />,
                        },
                    }}
                    isIcon
                    scope="single"
                />
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('occurDate', showDates.occurDate);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            occurDate: showDates.occurDate,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.PROCEDURE }, () => {
        listRefetch();
    });

    usePageTitle(i('page-title.cases.ttps', { number: caseData?.number || 0 }));

    return (
        <THLayout>
            <>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <AddButton label={i('procedure.create.label')} onClick={() => setVisibleCreate(true)} />
                            )}
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}

                            <ExportButton
                                onClick={() => {
                                    setExportDrawerVisible(true);
                                }}
                                objectType="Procedure"
                                selectedItems={dataSource.reduce((acc: string[], data) => {
                                    if (selectedKeys.includes(data['_id'])) {
                                        acc.push(data['_id']);
                                    }
                                    return acc;
                                }, [])}
                                queryParams={queryParams}
                                visible={exportDrawerVisible}
                                afterVisible={setExportDrawerVisible}
                                listNumber={table.count}
                            />

                            <SelectedInfo count={selectedKeys.length} label={i('common.selected')} />

                            <ActionList
                                actions={TTPActionList(
                                    dataSource.filter(({ _id }) => selectedKeys.includes(_id)),
                                    onSuccess,
                                )}
                                scope="list"
                                hidden={!selectedKeys?.length}
                            />
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            {!storagePage?.autoRefresh && (
                                <Badge dot={needRefresh}>
                                    <THTooltip
                                        title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                        placement="bottom"
                                    >
                                        <THButton
                                            size="small"
                                            type={needRefresh ? 'primary' : 'default'}
                                            shape="circle"
                                            onClick={() => {
                                                listRefetch();
                                                countRefetch();
                                                setNeedRefresh(false);
                                            }}
                                        >
                                            <ICONS.SyncOutlined />
                                        </THButton>
                                    </THTooltip>
                                </Badge>
                            )}
                            <ManageSwitch
                                label={i('common.auto-refresh')}
                                value={localAutoRefresh}
                                onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                                icon={<ICONS.SyncOutlined />}
                            />

                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={(editFilters): void => setLocalFilters(editFilters)}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.PROCEDURE]}>
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="_id"
                        rowSelection={hasPermissions ? rowSelection : undefined}
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('procedure.none')}>
                                {hasPermissions && (
                                    <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                        {i('procedure.create.label')}
                                    </THButton>
                                )}
                            </DisplayEmptyData>
                        }
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>

                {/* preview */}
                {data && visible && (
                    <TTPDrawer
                        visible={visible}
                        onVisibleChange={setVisible}
                        data={data}
                        eventList={eventList}
                        caseId={caseId}
                    />
                )}
                {/* create a TTP */}
                {visibleCreate && (
                    <TTPCreate
                        onFinish={({ patternList, occurDate, description }) => {
                            eventList.onCreate(
                                {
                                    patternList,
                                    occurDate,
                                    description,
                                },
                                caseId,
                            );
                        }}
                        visible={visibleCreate}
                        onVisibleChange={setVisibleCreate}
                    />
                )}
                {/* edit a TTP */}
                {visibleEdit && (
                    <TTPCreate
                        onFinish={({ patternList, occurDate, description }) => {
                            const defaultValue = dataSource.find((ttp) => ttp._id === defaultValues?._id);
                            let updatedFields = {};
                            if (occurDate !== defaultValue?.occurDate) updatedFields = { ...updatedFields, occurDate };
                            if (description !== defaultValue?.description)
                                updatedFields = { ...updatedFields, description };
                            if (
                                `${patternList[0].patternId}-${patternList[0].tactic}` !==
                                `${defaultValue?.patternId}-${defaultValue?.tactic}`
                            )
                                updatedFields = { ...updatedFields, ...patternList[0] };

                            eventList.onEdit(updatedFields, defaultValues?._id as string).then(() => listRefetch());
                        }}
                        defaultValues={defaultValues}
                        visible={visibleEdit}
                        onVisibleChange={setVisibleEdit}
                    />
                )}
            </>
        </THLayout>
    );
};

const MenuCount = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();
    const local = useLocalMessage();

    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery({
        controller: client.CaseController,
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'procedures' }],
                name: `count-case-ttps-${caseId}`,
            },
        },
        fetchOnStart: true,
    });

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.PROCEDURE }, (): void => {
        countRefetch();
    });

    const processedCount = count < 0 ? `+${Math.abs(count)}` : count;

    return (
        <>
            <ICONS.TTPIcon />
            <span className="tab-counter__label">
                {local('case.layout.nav-bar.procedures')} ({processedCount})
            </span>
        </>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.procedures',
        render: <MenuCount />,
    },
    path: RouteBuilder.compute('cases.procedures'),
    content: <TTPList />,
    exact: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.procedures' },
};
